import { Component, input, computed, output, signal, inject, OnDestroy, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, firstValueFrom, of } from 'rxjs';
import { AttachmentFile } from '#models';
import { FileType } from '#enums';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ImagePreviewDialog } from '#components';
import { PSSRResponseService, ToasterService } from '#services';
import { TranslateService } from '@ngx-translate/core';
import { AttachmentListComponent } from './attachment-list/attachment-list.component';

@Component({
  selector: 'app-attachments',
  imports: [DialogModule, AttachmentListComponent],
  templateUrl: './attachments.component.html',
  styleUrl: './attachments.component.scss',
})
export class AttachmentsComponent implements OnDestroy {
  // Primary input using AttachmentFile model
  files = input<AttachmentFile[]>([]);

  canDelete = input(true);
  canDownload = input(true);

  private _dialogService = inject(DialogService);
  private _pssrResponseService = inject(PSSRResponseService);
  private _toasterService = inject(ToasterService);
  private _translateService = inject(TranslateService);
  private _destroyRef = inject(DestroyRef);

  // Control whether to show links section
  showLinks = input<boolean>(true);

  // Output events for delete actions
  deleteImage = output<AttachmentFile>();
  deleteDocument = output<AttachmentFile>();
  deleteLink = output<AttachmentFile>();

  // Output event for download
  downloadFile = output<AttachmentFile>();

  // Output event for expanding an image that requires external (parent-level) loading
  expandImage = output<AttachmentFile>();

  // Fullscreen image preview state
  displayImage = signal(false);
  imagePhoto = signal<string>('');

  // Computed properties to filter files by type
  imageFiles = computed(() => this.files().filter(f => f.fileType === FileType.Image));

  documentFiles = computed(() => this.files().filter(f => f.fileType === FileType.Document));

  linkFiles = computed(() => this.files().filter(f => f.fileType === FileType.Link));

  private _imageDialogRef: DynamicDialogRef<ImagePreviewDialog> | null = null;
  private _loadingFullImage = signal<boolean>(false);
  // Cache for loaded full images (keyed by GUID)
  private _fullImageCache = signal<Map<string, string>>(new Map());

  btnExpandImage(image: AttachmentFile) {
    // Check if image already has full URL or is in cache
    const cachedImageUrl = image.guid ? this._fullImageCache().get(image.guid) : undefined;
    const imageUrlToUse = image.imageURL || cachedImageUrl;

    // If full image URL is available, use it
    if (imageUrlToUse) {
      this._openImageDialog(imageUrlToUse);
      return;
    }

    // If full image URL is not loaded yet, fetch it on-demand
    if (image.guid && image.responseItemId) {
      this._loadingFullImage.set(true);

      this._pssrResponseService
        .getImageByGUID(image.responseItemId, image.guid)
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          catchError(() => {
            this._toasterService.showError(this._translateService.instant('common.genericErrorMessage'));
            // Fallback to thumbnail if full image fails to load
            if (image.thumbnailURL) {
              this._openImageDialog(image.thumbnailURL);
            }
            return of(null);
          }),
          finalize(() => this._loadingFullImage.set(false))
        )
        .subscribe(fullImage => {
          if (fullImage) {
            const fullImageUri = fullImage.Photo.startsWith('data:')
              ? fullImage.Photo
              : `data:image/jpeg;base64,${fullImage.Photo}`;

            // Cache the full image URL (do NOT mutate input object)
            this._fullImageCache.update(cache => {
              const newCache = new Map(cache);
              newCache.set(image.guid!, fullImageUri);
              return newCache;
            });

            this._openImageDialog(fullImageUri);
          }
        });
    } else {
      // No imageURL and no responseItemId — delegate to parent for external loading
      this.expandImage.emit(image);
    }
  }

  private _openImageDialog(imageUri: string) {
    this._imageDialogRef = this._dialogService.open(ImagePreviewDialog, {
      dismissableMask: true,
      closable: true,
      draggable: false,
      resizable: false,
      styleClass: 'image-preview-dialog',
      data: {
        imageUri,
      },
    });
  }
  ngOnDestroy() {
    if (this._imageDialogRef) {
      this._imageDialogRef.close();
    }
  }

  async download(file: AttachmentFile) {
    // Handle download based on file type
    if (file.fileType === FileType.Link) {
      // For links, open in new tab
      window.open(file.file, '_blank');
      return;
    }

    // For images, fetch full image on-demand if not already available
    if (file.fileType === FileType.Image && !file.imageURL) {
      const cachedUrl = file.guid ? this._fullImageCache().get(file.guid) : undefined;
      if (cachedUrl) {
        this._triggerDownload(cachedUrl, file.fileName);
        return;
      }

      if (file.guid && file.responseItemId) {
        try {
          const fullImage = await firstValueFrom(
            this._pssrResponseService
              .getImageByGUID(file.responseItemId, file.guid)
              .pipe(takeUntilDestroyed(this._destroyRef))
          );
          if (fullImage) {
            const fullImageUri = fullImage.Photo.startsWith('data:')
              ? fullImage.Photo
              : `data:image/jpeg;base64,${fullImage.Photo}`;

            this._fullImageCache.update(cache => {
              const newCache = new Map(cache);
              newCache.set(file.guid!, fullImageUri);
              return newCache;
            });

            this._triggerDownload(fullImageUri, file.fileName);
            return;
          }
        } catch {
          this._toasterService.showError(this._translateService.instant('common.genericErrorMessage'));
          return;
        }
      }
    }

    // For documents and images with imageURL, emit event for parent to handle
    this.downloadFile.emit(file);
  }

  private _triggerDownload(dataUrl: string, fileName: string) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onDeleteImage(file: AttachmentFile) {
    this.deleteImage.emit(file);
  }

  onDeleteDocument(file: AttachmentFile) {
    this.deleteDocument.emit(file);
  }

  onDeleteLink(file: AttachmentFile) {
    this.deleteLink.emit(file);
  }
}
